import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Shield, ShieldOff, AlertTriangle, Clock, RefreshCw } from 'lucide-react';
import { tradingService } from '../../services/trading';
import { NukeButtonDialog } from './NukeButtonDialog';

/**
 * Emergency Stop Section for Settings page
 * Allows user to manually trigger or reset circuit breaker for all configs
 */
export function EmergencyStopSection() {
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Get all algorithm configs to check circuit breaker status
  const { data: configs = [], isLoading: isLoadingConfigs } = useQuery({
    queryKey: ['algorithmConfigs'],
    queryFn: () => tradingService.getAlgorithmConfigs(),
  });

  // Get circuit breaker status for each config
  const { data: statuses = [], isLoading: isLoadingStatuses } = useQuery({
    queryKey: ['circuitBreakerStatuses', configs.map(c => c.id)],
    queryFn: async () => {
      if (configs.length === 0) return [];
      const statusPromises = configs.map(config =>
        tradingService.getCircuitBreakerStatus(config.id).catch(() => null)
      );
      return Promise.all(statusPromises);
    },
    enabled: configs.length > 0,
  });

  // Check if ANY config has circuit breaker triggered
  const isAnyTriggered = statuses.some(status => status?.isTriggered);
  const triggeredCount = statuses.filter(status => status?.isTriggered).length;
  const triggeredAt = statuses.find(status => status?.isTriggered)?.triggeredAt;

  // Read autoReset from the first status that has a non-null value; default true
  const autoReset = statuses.find(status => status != null)?.autoReset ?? true;

  // Mutation to trigger all circuit breakers
  const triggerMutation = useMutation({
    mutationFn: () => tradingService.triggerCircuitBreakerAll(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['algorithmConfigs'] });
      queryClient.invalidateQueries({ queryKey: ['circuitBreakerStatuses'] });
      queryClient.invalidateQueries({ queryKey: ['circuitBreakerStatus'] });
      setIsDialogOpen(false);
    },
    onError: (error: any) => {
      console.error('Failed to trigger circuit breaker:', error);
      alert(error.message || 'Failed to trigger emergency stop');
    },
  });

  // Mutation to reset all circuit breakers
  const resetMutation = useMutation({
    mutationFn: () => tradingService.resetCircuitBreakerAll(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['algorithmConfigs'] });
      queryClient.invalidateQueries({ queryKey: ['circuitBreakerStatuses'] });
      queryClient.invalidateQueries({ queryKey: ['circuitBreakerStatus'] });
    },
    onError: (error: any) => {
      console.error('Failed to reset circuit breaker:', error);
      alert(error.message || 'Failed to resume trading');
    },
  });

  // Mutation to toggle auto-reset setting
  const autoResetMutation = useMutation({
    mutationFn: (enabled: boolean) => tradingService.setCircuitBreakerAutoReset(enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['circuitBreakerStatuses'] });
      queryClient.invalidateQueries({ queryKey: ['circuitBreakerStatus'] });
    },
    onError: (error: any) => {
      console.error('Failed to update auto-reset setting:', error);
      alert(error.message || 'Failed to update auto-reset setting');
    },
  });

  const isLoading = isLoadingConfigs || isLoadingStatuses;

  // No configs - show setup message
  if (!isLoading && configs.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-full">
            <Shield className="w-8 h-8 text-gray-400" />
          </div>
        </div>
        <p className="text-gray-500 dark:text-gray-400">
          No webhook configurations found.
        </p>
        <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
          Set up webhook trading above to enable emergency stop.
        </p>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Card */}
      <motion.div
        className={`rounded-xl p-6 border-2 transition-colors ${
          isAnyTriggered
            ? 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900'
            : 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-900'
        }`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Status Icon */}
            <div className={`p-3 rounded-full ${
              isAnyTriggered
                ? 'bg-red-100 dark:bg-red-900/50'
                : 'bg-green-100 dark:bg-green-900/50'
            }`}>
              {isAnyTriggered ? (
                <ShieldOff className="w-6 h-6 text-red-600 dark:text-red-400" />
              ) : (
                <Shield className="w-6 h-6 text-green-600 dark:text-green-400" />
              )}
            </div>

            {/* Status Text */}
            <div>
              <h3 className={`text-lg font-semibold ${
                isAnyTriggered
                  ? 'text-red-700 dark:text-red-400'
                  : 'text-green-700 dark:text-green-400'
              }`}>
                {isAnyTriggered ? 'Trading Blocked' : 'Trading Active'}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {isAnyTriggered
                  ? `${triggeredCount} of ${configs.length} config(s) stopped`
                  : `${configs.length} webhook config(s) active`
                }
              </p>
            </div>
          </div>

          {/* Status Badge */}
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            isAnyTriggered
              ? 'bg-red-200 dark:bg-red-900 text-red-800 dark:text-red-200'
              : 'bg-green-200 dark:bg-green-900 text-green-800 dark:text-green-200'
          }`}>
            {isAnyTriggered ? '🛑 STOPPED' : '✅ ACTIVE'}
          </div>
        </div>

        {/* Triggered timestamp */}
        {isAnyTriggered && triggeredAt && (
          <div className="mt-4 flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
            <Clock className="w-4 h-4" />
            <span>
              Triggered at {new Date(triggeredAt).toLocaleTimeString()} on{' '}
              {new Date(triggeredAt).toLocaleDateString()}
            </span>
          </div>
        )}
      </motion.div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4">
        {isAnyTriggered ? (
          <>
            {/* Resume Trading Button */}
            <button
              onClick={() => resetMutation.mutate()}
              disabled={resetMutation.isPending}
              className="flex-1 flex items-center justify-center space-x-2 px-6 py-4 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {resetMutation.isPending ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  <span>Resuming...</span>
                </>
              ) : (
                <>
                  <Shield className="w-5 h-5" />
                  <span>Resume Trading</span>
                </>
              )}
            </button>
          </>
        ) : (
          <>
            {/* Emergency Stop Button */}
            <button
              onClick={() => setIsDialogOpen(true)}
              className="flex-1 flex items-center justify-center space-x-2 px-6 py-4 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold transition-colors shadow-lg hover:shadow-red-500/25"
            >
              <AlertTriangle className="w-5 h-5" />
              <span>Emergency Stop</span>
            </button>

            {/* Description */}
            <p className="flex-1 text-sm text-gray-500 dark:text-gray-400 flex items-center">
              Block all webhook trades immediately. Use in case of emergency or market volatility.
            </p>
          </>
        )}
      </div>

      {/* Auto-Reset Toggle */}
      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-3">
          <Clock className="w-5 h-5 text-gray-400 flex-shrink-0" />
          <div>
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              Auto-reset at midnight PT
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {autoReset
                ? 'Auto-resets at midnight PT'
                : 'Stays stopped until you tap Resume Trading'}
            </div>
          </div>
        </div>
        <button
          onClick={() => autoResetMutation.mutate(!autoReset)}
          disabled={autoResetMutation.isPending}
          aria-label={autoReset ? 'Disable auto-reset' : 'Enable auto-reset'}
          className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors disabled:opacity-50 ${
            autoReset ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
          }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
              autoReset ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {/* Nuke Button Dialog */}
      <NukeButtonDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onConfirm={() => triggerMutation.mutate()}
        isLoading={triggerMutation.isPending}
      />
    </div>
  );
}

